/* 
Formatting utility functions.

Although they can be called directly as required, most of the time it is more convenient to
use the corresponding filter (see filter.mixins.js).
*/
import _ from "underscore";
import qs from "qs";
import moment from "moment";

export function compactDate(dateAsString) {
  const d = moment(dateAsString);
  return d.isValid() ? d.format("DD-MM-YYYY") : "";
}

export function compactDateTime(dateAsString) {
  const d = moment(dateAsString);
  return d.isValid() ? d.format("DD-MM-YYYY h:mm:ss a") : "";
}

export function compactDateOrDateTime(dateAsString) {
  const d = moment(dateAsString);
  if (!d.isValid()) return "";
  const s = d.format("DD-MM-YYYY h:mm:ss a");
  return s.endsWith("12:00:00 am") ? d.format("DD-MM-YYYY") : s;
}

export function squishedDateTime(dateAsString) {
  const d = moment(dateAsString);
  return d.isValid() ? d.format("D/M/YY h:mma") : "";
}

export function friendlyDateTime(dateAsString, now = new Date()) {
  const d = moment(dateAsString);
  const time = d.format("h:mm:ss a");
  if (d.isSame(moment(now), "day")) {
    return `today at ${time}`;
  }
  const yesterday = moment(now).subtract(1, "days");
  if (d.isSame(yesterday, "day")) {
    return `yesterday at ${time}`;
  }
  const sixDaysAgo = moment(now).subtract(6, "days");
  if (d.isSameOrAfter(sixDaysAgo, "day")) {
    return `${d.format("dddd")} at ${time}`;
  }
  if (d.isSame(moment(now), "year")) {
    return `${d.format("Do MMMM")} at ${time}`;
  }
  return `${d.format("Do MMMM YYYY")} at ${time}`;
}

export function longDate(dateAsString) {
  const d = moment(dateAsString);
  return d.isValid() ? d.format("dddd DD MMM YYYY") : "";
}

export function longDateTime(dateAsString) {
  const d = moment(dateAsString);
  return d.isValid() ? d.format("dddd DD MMM YYYY h:mm:ss a") : "";
}

export function formatNumber(number) {
  return isNumber(number) ? addCommas(getNumber(number, 0)) : "";
}

export function currency(number, prefix = "$") {
  return isNumber(number) ? prefix + addCommas(getNumber(number, 2)) : "";
}

export function currency0(number, prefix = "$") {
  return isNumber(number) ? prefix + addCommas(getNumber(number, 0)) : "";
}

export function numericInput(number, decimalPlaces) {
  return isNumber(number) ? getNumber(number, decimalPlaces) : "";
}

export function numericInput1(number) {
  return numericInput(number, 1);
}

export function numericInput2(number) {
  return numericInput(number, 2);
}

export function numericInput3(number) {
  return numericInput(number, 3);
}

export function percentage(percent, decimalPlaces = null) {
  if (!isNumber(percent)) return "";
  const percentage = round(percent * 100, 12);
  if (decimalPlaces === null) {
    for (decimalPlaces = 0; decimalPlaces < 10; decimalPlaces++) {
      if (round(percentage, decimalPlaces) == percentage) break;
    }
  }
  return getNumber(percentage, decimalPlaces) + "%";
}

export function round(number, decimalPlaces) {
  return Number(getNumber(number, decimalPlaces));
}

function isNumber(number) {
  if (isNaN(number) || number === null || number === "") {
    return false;
  } else {
    return true;
  }
}

function getNumber(number, decimalPlaces) {
  return Number(number).toFixed(decimalPlaces);
}

// http://www.mredkj.com/javascript/numberFormat.html
function addCommas(nStr) {
  nStr += "";
  const x = nStr.split(".");
  let x1 = x[0];
  let x2 = x.length > 1 ? "." + x[1] : "";
  var rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, "$1" + "," + "$2");
  }
  return x1 + x2;
}

export function stringifyObject(object) {
  return qs.stringify(object, { arrayFormat: "repeat", allowDots: true });
}

export function yesNo(boolean) {
  return boolean ? "Yes" : boolean == null || boolean == undefined ? "" : "No";
}

export function trueFalse(boolean) {
  return boolean ? "True" : boolean == null || boolean == undefined ? "" : "False";
}

export function timespan(timespanAsString) {
  if (!timespanAsString) {
    return "";
  }
  const duration = moment.duration(timespanAsString);
  if (duration.asMilliseconds() < 1000) {
    return "less than a second";
  }
  if (duration.asSeconds() < 60) {
    return `${duration.seconds()} seconds`;
  }
  if (duration.asMinutes() < 60) {
    return `${duration.minutes()} minutes and ${duration.seconds()} seconds`;
  }
  return `${duration.hours()} hours, ${duration.minutes()} minutes and ${duration.seconds()} seconds`;
}

export function explanation(s) {
  if (s) {
    if (!~[".", "!", "?", ";"].indexOf(s[s.length - 1])) {
      s += ".";
    }
    return `${s[0].toUpperCase()}${s.substring(1)}`;
  }
  return s;
}

function isUpperCase(s) {
  return s == s.toUpperCase() && s != s.toLowerCase();
}

export function startWithLowerCase(s) {
  if (s) {
    if (isUpperCase(s[0]) && (s.length == 1 || !isUpperCase(s[1]))) {
      return `${s[0].toLowerCase()}${s.substring(1)}`;
    }
  }
  return s;
}

export function sentenceCase(s) {
  if (s) {
    return `${s[0].toUpperCase()}${s.substring(1).toLowerCase()}`;
  }
  return s;
}

export function stripRequiredIndicator(label) {
  return label ? label.replace(/\*$/, "") : null;
}

export function splitIntoWords(string) {
  var array = string.split(/(?=[A-Z])/).map(a => a.toLowerCase());
  if (array.length > 0) {
    array[0] = array[0][0].toUpperCase() + array[0].substr(1);
    return array.join(" ");
  }
  return "";
}

export function fullStop(s) {
  return !s || s.endsWith(".") ? s : s + ".";
}

export function getDecimalPlacesForPrecision(value, decimalPlaces) {
  const rounded = getNumber(value, decimalPlaces);
  const roundedNumber = Number(rounded);
  let isPrecise = value === roundedNumber;
  while (!isPrecise) {
    decimalPlaces++;
    isPrecise = Number(getNumber(value, decimalPlaces)) !== roundedNumber;
  }
  return decimalPlaces;
}

export function isNullOrWhitespace(input) {
  if (typeof input == "number") return false;
  return !input || !input.trim();
}

export function isSame(b1, b2) {
  return b1 == b2 || (isNullOrWhitespace(b1) && isNullOrWhitespace(b2));
}

export function getRiskFactorLowerBoundDescription(factor, allFactors, riskFactorType) {
  const bounds = _(allFactors)
    .chain()
    .reject(f => f.id == factor.id)
    .union([factor])
    .sortBy(f => (f.lowerBound === null ? -99999 : f.lowerBound))
    .reverse()
    .value();
  const isDuplicate = f =>
    _(bounds).some(b => isSame(b.lowerBound, f.lowerBound) && b.id != f.id && f.id != null && f.id != factor.id);
  const sortedBounds = _(bounds).reject(f => isDuplicate(f));
  const index = _(sortedBounds).findIndex(f => f.id == factor.id);
  const isFirst = index === 0;
  const isLast = index === sortedBounds.length - 1;
  const previousLowerBound = !isFirst ? sortedBounds[index - 1].lowerBound : null;
  if (isFirst && isLast && factor.lowerBound === null) {
    return "Any value";
  }
  if (riskFactorType.attributes.includes("IsBoolean")) {
    return factor.lowerBound === 0 ? "False" : "True";
  }
  const lowerBoundExclusive = riskFactorType.attributes.includes("LowerBoundIsExclusive");
  if (lowerBoundExclusive) {
    if (isLast && !isFirst) {
      return `${formatBound(previousLowerBound, riskFactorType)} or lower`;
    } else if (isFirst) {
      return `>${formatBound(factor.lowerBound, riskFactorType)}`;
    } else {
      return `>${formatBound(factor.lowerBound, riskFactorType)} to ${formatBound(previousLowerBound, riskFactorType)}`;
    }
  } else {
    if (isLast && !isFirst) {
      return `Less than ${formatBound(previousLowerBound, riskFactorType)}`;
    } else if (isFirst) {
      return `${formatBound(factor.lowerBound, riskFactorType)} or higher`;
    } else {
      return `${formatBound(factor.lowerBound, riskFactorType)} to <${formatBound(previousLowerBound, riskFactorType)}`;
    }
  }
}

function formatBound(bound, riskFactorType) {
  if (riskFactorType.attributes.includes("IsCurrency")) {
    return currency0(bound);
  } else if (riskFactorType.attributes.includes("IsPercentage")) {
    return percentage(bound, 1);
  } else {
    return bound;
  }
}
